import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  ArrowRight,
  Building2,
  Calendar,
  CheckCircle2,
  Clock,
  FileText,
  Monitor,
  MessageCircle,
  ShieldCheck,
  Store,
  Tag,
  Usb,
  User,
  Zap,
} from "lucide-react";
import { certificateDataByType, formatBRL, type PersonType } from "@/lib/products";
import { findCertificateProduct, loadCatalogProducts } from "@/lib/catalog";
import type { Product } from "@/types/api";

type CertType = "A1" | "A3";

interface PersonMeta {
  key: PersonType;
  label: string;
  shortLabel: string;
  icon: typeof User;
  description: string;
  storeRoute: string;
  productKind: "ECPF" | "ECNPJ" | "EMEI";
}

// Ordem intencional: PJ primeiro (maior volume de conversão — empresas
// precisam do e-CNPJ para NF-e/eSocial), depois PF e MEI. Bate com o
// default state do componente.
const PERSON_META: PersonMeta[] = [
  {
    key: "PJ",
    label: "Pessoa Jurídica",
    shortLabel: "PJ",
    icon: Building2,
    description: "Para empresas que emitem NF-e, eSocial, SEFAZ e operam em nome da empresa.",
    storeRoute: "/certificado-pj",
    productKind: "ECNPJ",
  },
  {
    key: "PF",
    label: "Pessoa Física",
    shortLabel: "PF",
    icon: User,
    description: "Para quem assina contratos, declara IR ou acessa o Gov.br.",
    storeRoute: "/certificado-pf",
    productKind: "ECPF",
  },
  {
    key: "MEI",
    label: "MEI",
    shortLabel: "MEI",
    icon: Store,
    description: "Para microempreendedores que emitem NFS-e e acessam o Portal do Empreendedor.",
    storeRoute: "/certificado-mei",
    productKind: "EMEI",
  },
];

const WHERE_TO_USE_ICONS = [
  { label: "Receita Federal (e-CAC)", icon: ShieldCheck },
  { label: "Emissão de NF-e / NFS-e", icon: FileText },
  { label: "Bancos e Gov.br", icon: Building2 },
  { label: "Assinatura de documentos", icon: CheckCircle2 },
];

export const CertificateInfo = () => {
  // PJ abre como default porque o e-CNPJ é o segmento de maior conversão
  // (empresas precisam pra NF-e, eSocial, SEFAZ). O usuário PF sempre pode
  // trocar com um clique, mas a primeira impressão é otimizada pra B2B.
  const [personType, setPersonType] = useState<PersonType>("PJ");
  const [certificateType, setCertificateType] = useState<CertType>("A1");
  const [catalog, setCatalog] = useState<Product[]>([]);
  const navigate = useNavigate();

  useEffect(() => {
    void loadCatalogProducts()
      .then((c) => setCatalog(c))
      .catch(() => undefined);
  }, []);

  const currentMeta = useMemo(
    () => PERSON_META.find((p) => p.key === personType)!,
    [personType]
  );

  const currentData = certificateDataByType[personType][certificateType];

  const currentProduct = useMemo(
    () => findCertificateProduct(catalog, currentMeta.productKind, certificateType),
    [catalog, currentMeta.productKind, certificateType]
  );

  const validityYears = certificateType === "A1" ? "1 ano" : "2 anos";
  const mediaLabel = certificateType === "A1" ? "Arquivo digital (.pfx)" : "Token USB ou em nuvem";
  const emissionTime =
    certificateType === "A1" ? "~15 min após validação" : "Mesmo dia após validação";

  // Promotion-aware pricing derived from the catalog (which already has
  // promotions applied server-side).
  const hasPromo =
    !!currentProduct &&
    typeof currentProduct.originalPriceCents === "number" &&
    currentProduct.originalPriceCents > currentProduct.priceCents;

  const discountPct = hasPromo
    ? Math.round(
        (1 - currentProduct!.priceCents / currentProduct!.originalPriceCents!) * 100
      )
    : null;

  const savings = hasPromo
    ? (currentProduct!.originalPriceCents! - currentProduct!.priceCents) / 100
    : null;

  // Live countdown to the promotion end, only when applicable
  const [countdown, setCountdown] = useState<{ d: number; h: number; m: number } | null>(null);
  useEffect(() => {
    if (!currentProduct?.promotion?.endsAt) {
      setCountdown(null);
      return;
    }
    const tick = () => {
      const end = new Date(currentProduct.promotion!.endsAt).getTime();
      const diff = end - Date.now();
      if (diff <= 0) {
        setCountdown(null);
        return;
      }
      setCountdown({
        d: Math.floor(diff / 86_400_000),
        h: Math.floor((diff % 86_400_000) / 3_600_000),
        m: Math.floor((diff % 3_600_000) / 60_000),
      });
    };
    tick();
    const id = setInterval(tick, 30_000);
    return () => clearInterval(id);
  }, [currentProduct?.promotion?.endsAt]);

  const handleViewProduct = () => {
    if (currentProduct) {
      navigate(`/produto/${currentProduct.slug}`);
    } else {
      navigate(currentMeta.storeRoute);
    }
  };

  const handleWhatsApp = () => {
    const msg = encodeURIComponent(
      `Olá! Tenho interesse no ${currentMeta.label === "MEI" ? "certificado MEI" : "certificado " + currentMeta.label} Tipo ${certificateType}. Pode me ajudar?`
    );
    window.open(`https://wa.me/551152397247?text=${msg}`, "_blank");
  };

  return (
    <section id="sobre" className="py-12 md:py-20 bg-gradient-to-b from-background to-muted/30">
      <div className="container max-w-6xl">
        {/* Header */}
        <div className="text-center mb-6 md:mb-10 space-y-2 md:space-y-3">
          <p className="text-[10px] md:text-xs font-bold uppercase tracking-wider text-primary">
            Entenda os certificados
          </p>
          <h2 className="text-2xl md:text-4xl font-extrabold text-foreground leading-tight">
            Certificado <span className="text-primary">pra cada perfil</span>
          </h2>
          <p className="text-sm md:text-base text-muted-foreground max-w-2xl mx-auto">
            Escolha o tipo de pessoa e o modelo pra ver onde funciona e quanto custa.
          </p>
        </div>

        {/* Person-type tabs */}
        <div className="mb-6 flex flex-wrap justify-center gap-2">
          {PERSON_META.map((p) => {
            const Icon = p.icon;
            const active = personType === p.key;
            return (
              <button
                key={p.key}
                type="button"
                onClick={() => setPersonType(p.key)}
                className={`inline-flex items-center gap-2 rounded-full border-2 px-5 py-2.5 text-sm font-bold transition-all ${
                  active
                    ? "bg-primary text-primary-foreground border-primary shadow-md scale-105"
                    : "bg-white text-foreground border-border hover:border-primary/40 hover:bg-muted/40"
                }`}
              >
                <Icon className="h-4 w-4" />
                {p.label}
              </button>
            );
          })}
        </div>

        {/* A1 / A3 toggle */}
        <div className="mb-8 flex justify-center">
          <div className="inline-flex rounded-full bg-muted p-1">
            {(["A1", "A3"] as const).map((t) => {
              const active = certificateType === t;
              return (
                <button
                  key={t}
                  type="button"
                  onClick={() => setCertificateType(t)}
                  className={`inline-flex items-center gap-2 rounded-full px-5 py-2 text-sm font-bold transition-all ${
                    active
                      ? "bg-white text-foreground shadow-sm"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {t === "A1" ? <Monitor className="h-4 w-4" /> : <Usb className="h-4 w-4" />}
                  Tipo {t}
                </button>
              );
            })}
          </div>
        </div>

        {/* Content grid — summary card (sticky left) + info cards (right) */}
        <div className="grid lg:grid-cols-[minmax(0,2fr)_minmax(0,3fr)] gap-8">
          {/* Summary card */}
          <div className="lg:sticky lg:top-24 h-fit">
            <div
              className={`relative rounded-3xl border-2 bg-card p-6 md:p-8 space-y-5 shadow-lg overflow-hidden transition-all ${
                hasPromo ? "border-warning/40 ring-2 ring-warning/15" : "border-primary/20"
              }`}
            >
              {/* Promotion banner slash on top — only when there's an active promo */}
              {hasPromo && currentProduct?.promotion && (
                <>
                  <div className="absolute -top-px left-0 right-0 h-1 bg-gradient-to-r from-warning via-warning/60 to-warning" />
                  <div className="absolute -right-10 top-5 rotate-45 bg-warning px-10 py-1 text-[10px] font-extrabold uppercase tracking-wider text-warning-foreground shadow-md">
                    −{discountPct}% OFF
                  </div>
                </>
              )}

              {/* Campaign header (only when promotion is active) */}
              {hasPromo && currentProduct?.promotion && (
                <div className="-mx-6 -mt-6 md:-mx-8 md:-mt-8 px-6 md:px-8 py-3 bg-gradient-to-r from-warning/20 via-warning/10 to-warning/20 border-b border-warning/30">
                  <div className="flex items-center gap-2">
                    <Tag className="h-3.5 w-3.5 text-warning shrink-0" />
                    <p className="text-[11px] font-extrabold uppercase tracking-wider text-foreground">
                      {currentProduct.promotion.badgeLabel ?? currentProduct.promotion.name}
                    </p>
                  </div>
                  {countdown && (
                    <div className="mt-1 flex items-center gap-1.5 text-[11px] text-foreground/70">
                      <Clock className="h-3 w-3" />
                      <span>
                        Termina em{" "}
                        <strong className="text-foreground tabular-nums">
                          {countdown.d > 0 ? `${countdown.d}d ` : ""}
                          {countdown.h}h {countdown.m}m
                        </strong>
                      </span>
                    </div>
                  )}
                </div>
              )}

              {/* Icon + type */}
              <div className="flex items-center gap-3">
                <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-brand text-white shadow-md">
                  {certificateType === "A1" ? (
                    <Monitor className="h-7 w-7" />
                  ) : (
                    <Usb className="h-7 w-7" />
                  )}
                </div>
                <div>
                  <p className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
                    {currentMeta.label}
                  </p>
                  <p className="text-2xl font-extrabold text-foreground leading-tight">
                    e-{currentMeta.productKind === "ECPF"
                      ? "CPF"
                      : currentMeta.productKind === "ECNPJ"
                      ? "CNPJ"
                      : "MEI"}{" "}
                    {certificateType}
                  </p>
                </div>
              </div>

              {/* Feature chips */}
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm">
                  <Calendar className="h-4 w-4 text-primary shrink-0" />
                  <span>
                    Validade <strong className="text-foreground">{validityYears}</strong>
                  </span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  {certificateType === "A1" ? (
                    <Monitor className="h-4 w-4 text-primary shrink-0" />
                  ) : (
                    <Usb className="h-4 w-4 text-primary shrink-0" />
                  )}
                  <span>
                    Formato <strong className="text-foreground">{mediaLabel}</strong>
                  </span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Zap className="h-4 w-4 text-warning shrink-0" />
                  <span>
                    Emissão <strong className="text-foreground">{emissionTime}</strong>
                  </span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <ShieldCheck className="h-4 w-4 text-success shrink-0" />
                  <span>
                    <strong className="text-foreground">ICP-Brasil</strong> · validade jurídica plena
                  </span>
                </div>
              </div>

              {/* Price block — visually rich, with strikethrough on promo */}
              <div
                className={`rounded-2xl p-5 text-center space-y-1 ${
                  hasPromo
                    ? "bg-gradient-to-br from-warning/10 via-warning/5 to-background border-2 border-warning/30"
                    : "bg-muted/50"
                }`}
              >
                {currentProduct ? (
                  <>
                    {hasPromo ? (
                      <>
                        <div className="flex items-baseline justify-center gap-2">
                          <span className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">
                            De
                          </span>
                          <span className="text-base text-muted-foreground line-through tabular-nums font-semibold">
                            {formatBRL(currentProduct.originalPriceCents! / 100)}
                          </span>
                        </div>
                        <div className="flex items-baseline justify-center gap-2">
                          <span className="text-[11px] uppercase tracking-wider font-bold text-success">
                            Por apenas
                          </span>
                        </div>
                        <div className="text-5xl font-black text-foreground tabular-nums leading-none py-1">
                          {formatBRL(currentProduct.priceCents / 100)}
                        </div>
                        {savings !== null && (
                          <div className="inline-flex items-center gap-1 rounded-full bg-success/15 px-3 py-1 text-xs font-extrabold text-success">
                            🎉 Você economiza {formatBRL(savings)}
                          </div>
                        )}
                      </>
                    ) : (
                      <>
                        <p className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">
                          A partir de
                        </p>
                        <div className="text-4xl font-extrabold text-foreground tabular-nums">
                          {formatBRL(currentProduct.priceCents / 100)}
                        </div>
                      </>
                    )}
                    <p className="text-[11px] text-muted-foreground mt-1.5">
                      à vista no PIX · até 12x no cartão
                    </p>
                  </>
                ) : (
                  <p className="text-sm text-muted-foreground py-2">Consulte preços</p>
                )}
              </div>

              {/* CTAs */}
              <div className="space-y-2">
                <Button
                  size="lg"
                  className={`w-full font-bold shadow-md hover:shadow-lg ${
                    hasPromo ? "bg-success hover:bg-success/90" : ""
                  }`}
                  onClick={handleViewProduct}
                  disabled={!currentProduct}
                >
                  {hasPromo ? "Aproveitar o desconto" : "Ver produto"}{" "}
                  <ArrowRight className="h-4 w-4 ml-1.5" />
                </Button>
                <div className="grid grid-cols-2 gap-2">
                  <Button variant="outline" size="sm" asChild>
                    <Link to={currentMeta.storeRoute}>Ver planos</Link>
                  </Button>
                  <Button variant="outline" size="sm" onClick={handleWhatsApp}>
                    <MessageCircle className="h-3.5 w-3.5 mr-1" /> WhatsApp
                  </Button>
                </div>
              </div>
            </div>
          </div>

          {/* Right: info cards (always visible, no accordion) */}
          <div className="space-y-5">
            {/* Description */}
            <div className="rounded-2xl border bg-card p-6 space-y-3">
              <div className="flex items-center gap-2">
                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10 text-primary">
                  <currentMeta.icon className="h-4 w-4" />
                </div>
                <h3 className="font-bold text-lg text-foreground">{currentMeta.label}</h3>
              </div>
              <p className="text-sm text-muted-foreground leading-relaxed">
                {currentData.description}
              </p>
            </div>

            {/* Onde utilizar */}
            <div className="rounded-2xl border bg-card p-6 space-y-4">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-success" />
                <h3 className="font-bold text-foreground">Onde você vai usar</h3>
              </div>

              {/* Visual icons row */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {WHERE_TO_USE_ICONS.map((w) => {
                  const Icon = w.icon;
                  return (
                    <div
                      key={w.label}
                      className="flex flex-col items-center gap-2 rounded-xl bg-muted/40 p-3 text-center"
                    >
                      <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-white shadow-sm">
                        <Icon className="h-4 w-4 text-primary" />
                      </div>
                      <p className="text-[11px] font-semibold text-foreground leading-tight">
                        {w.label}
                      </p>
                    </div>
                  );
                })}
              </div>

              {/* Full list (compact) */}
              <details className="group">
                <summary className="cursor-pointer text-xs font-bold text-primary hover:underline list-none">
                  Ver lista completa +
                </summary>
                <ul className="mt-3 space-y-1.5 pl-1">
                  {currentData.whereToUse.map((item, i) => (
                    <li key={i} className="text-xs text-muted-foreground leading-relaxed">
                      {item}
                    </li>
                  ))}
                </ul>
              </details>
            </div>

            {/* Documentos necessários */}
            <div className="rounded-2xl border bg-card p-6 space-y-4">
              <div className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-primary" />
                <h3 className="font-bold text-foreground">Documentos necessários</h3>
              </div>
              <ul className="space-y-3">
                {currentData.documents.map((item, i) => (
                  <li key={i} className="text-sm text-muted-foreground leading-relaxed">
                    <span
                      // Markdown básico: **bold** e [link](url)
                      dangerouslySetInnerHTML={{
                        __html: item
                          .replace(/^•\s*/, "")
                          .replace(/\*\*(.+?)\*\*/g, '<strong class="text-foreground">$1</strong>')
                          .replace(
                            /\[([^\]]+)\]\(([^)]+)\)/g,
                            '<a href="$2" target="_blank" rel="noopener noreferrer" class="text-primary font-semibold underline">$1</a>'
                          ),
                      }}
                    />
                  </li>
                ))}
              </ul>
              <div className="rounded-lg bg-primary/5 border border-primary/20 p-3 text-xs text-foreground">
                💡 <strong>Emissão online:</strong> tudo por videoconferência sem sair de casa — CNH após 2017 obrigatória.
                <br />
                🏢 <strong>Emissão presencial:</strong> na <strong>Rua da Glória, 108 — Liberdade, São Paulo/SP</strong>.
              </div>
            </div>

            {/* Compatibilidade */}
            <div className="rounded-2xl border bg-card p-6 space-y-3">
              <div className="flex items-center gap-2">
                <Monitor className="h-5 w-5 text-primary" />
                <h3 className="font-bold text-foreground">Compatibilidade</h3>
              </div>
              <div className="flex flex-wrap gap-2">
                {currentData.compatibility.map((item, i) => (
                  <span
                    key={i}
                    className="inline-flex items-center gap-1.5 rounded-full bg-muted/50 border px-3 py-1 text-xs font-semibold text-foreground"
                  >
                    <CheckCircle2 className="h-3 w-3 text-success" />
                    {item.replace(/^•\s*/, "")}
                  </span>
                ))}
              </div>
              <p className="text-[11px] text-muted-foreground">
                Funciona em todos os navegadores modernos — Chrome, Firefox, Edge, Safari.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
